import {
  MD3DarkTheme,
  MD3LightTheme as DefaultTheme,
  MD3Theme,
} from 'react-native-paper';
import { Colors } from './colors';
import { roleColorValues, type ThemeMode } from './roleTokenValues';

/**
 * Generate theme with optional Inter fonts
 * @param useInterFonts - If true, uses Inter font family. If false, uses system default.
 * @param mode - 'light' keeps the MD3 light palette byte-identical to what
 * production has always rendered. 'dark' starts from MD3DarkTheme (so the
 * long tail of Paper internals gets sane dark defaults) and overrides the
 * keys Paper actually paints surfaces and text with, mapping them onto the
 * Crop Circle role tokens; without the override those keys carry Material's
 * purple-tinted ramp, which clashes with the hue-free Gripp dark palette.
 *
 * The overrides read `roleColorValues.dark` (static hex), NOT `Colors.role`:
 * on web `Colors.role` carries `var()` strings, and Paper runs its theme
 * colors through the `color` library for ripple/alpha math, which throws
 * `Unable to parse color from string: var(--x)` and error-boundaries the
 * whole app. The override only exists when the resolved mode is dark, so the
 * static dark values are always the correct ones; the theme re-renders when
 * the mode flips because callers pass `resolvedTheme` in.
 */
export const createBaseTheme = (
  useInterFonts = false,
  mode: ThemeMode = 'light',
): MD3Theme => {
  const fontFamily = useInterFonts ? 'Inter' : undefined;
  const base = mode === 'dark' ? MD3DarkTheme : DefaultTheme;
  const dark = roleColorValues.dark;

  // `Colors.role` is a nested object, and on web its values are `var()`
  // strings. Nothing in Paper reads `colors.role`, so spreading it in was
  // inert - but it seeded `MD3Theme['colors']` with the one value shape
  // Paper's colour maths cannot parse, one internal iteration away from the
  // crash `useRoleHex` exists to prevent. Keep the theme flat and hex-only.
  const { role: _role, ...flatColors } = Colors;

  return {
    ...base,
    colors: {
      ...base.colors,
      ...flatColors,
      ...(mode === 'dark' && {
        background: dark.canvas,
        onBackground: dark.textPrimary,
        surface: dark.surface,
        onSurface: dark.textPrimary,
        surfaceVariant: dark.surfaceRaised,
        onSurfaceVariant: dark.textSecondary,
        outline: dark.borderControl,
        outlineVariant: dark.borderSubtle,
        onPrimary: dark.textOnBrand,
        inverseSurface: dark.surfaceInverse,
        inverseOnSurface: dark.textOnInverse,
        elevation: {
          level0: 'transparent',
          level1: dark.surface,
          level2: dark.surfaceRaised,
          level3: dark.surfaceRaised,
          level4: dark.surfaceSelected,
          level5: dark.surfaceActive,
        },
      }),
    },
    roundness: 8,
    fonts: {
      ...DefaultTheme.fonts,
      default: {
        ...DefaultTheme.fonts.default,
        ...(fontFamily && { fontFamily }),
      },
      displayLarge: {
        ...DefaultTheme.fonts.displayLarge,
        ...(fontFamily && { fontFamily }),
      },
      displayMedium: {
        ...DefaultTheme.fonts.displayMedium,
        ...(fontFamily && { fontFamily }),
      },
      displaySmall: {
        ...DefaultTheme.fonts.displaySmall,
        ...(fontFamily && { fontFamily }),
      },
      headlineLarge: {
        ...DefaultTheme.fonts.headlineLarge,
        ...(fontFamily && { fontFamily }),
      },
      headlineMedium: {
        ...DefaultTheme.fonts.headlineMedium,
        ...(fontFamily && { fontFamily }),
      },
      headlineSmall: {
        ...DefaultTheme.fonts.headlineSmall,
        ...(fontFamily && { fontFamily }),
      },
      titleLarge: {
        ...DefaultTheme.fonts.titleLarge,
        ...(fontFamily && { fontFamily }),
      },
      titleMedium: {
        ...DefaultTheme.fonts.titleMedium,
        ...(fontFamily && { fontFamily }),
      },
      titleSmall: {
        ...DefaultTheme.fonts.titleSmall,
        ...(fontFamily && { fontFamily }),
      },
      bodyLarge: {
        ...DefaultTheme.fonts.bodyLarge,
        ...(fontFamily && { fontFamily }),
      },
      bodyMedium: {
        ...DefaultTheme.fonts.bodyMedium,
        ...(fontFamily && { fontFamily }),
      },
      bodySmall: {
        ...DefaultTheme.fonts.bodySmall,
        ...(fontFamily && { fontFamily }),
      },
      labelLarge: {
        ...DefaultTheme.fonts.labelLarge,
        ...(fontFamily && { fontFamily }),
      },
      labelMedium: {
        ...DefaultTheme.fonts.labelMedium,
        ...(fontFamily && { fontFamily }),
      },
      labelSmall: {
        ...DefaultTheme.fonts.labelSmall,
        ...(fontFamily && { fontFamily }),
      },
    },
  };
};

// Default export with Inter fonts enabled
export const BaseTheme: MD3Theme = createBaseTheme(false);
